<script setup>
import { usePageHead } from '@composables/use-page-head'
import { ref } from 'vue'
import localSave from '@utils/local-save'
import postAuthRedirect from '@utils/post-auth-redirect'
import Layout from '@layouts/login'
import SocialLogin from '@views/auth/social-login'
import { email as emailValidator, required, useFormValidation } from '@composables/use-form-validation'
import { useAppearanceStore } from '@state/stores/appearance'
import { useAuthStore } from '@state/stores/auth'
import { useMessages } from '@composables/use-messages'

const getMessageText = useMessages()
const appearanceStore = useAppearanceStore()
const authStore = useAuthStore()
const email = ref('')
const password = ref('')
const checking = ref(false)
const validation = useFormValidation({
    email: { value: email, validators: [required, emailValidator] },
    password: { value: password, validators: [required] },
})

function notify(messageKey, type) {
    appearanceStore.handleNotification({
        message: getMessageText(messageKey),
        type,
    })
}

function clearFields() {
    email.value = ''
    password.value = ''
    validation.$reset()
}

function handleSuccess() {
    checking.value = false
    postAuthRedirect.resolveAfterAuth(localSave.get('redirectURL') || '/')
}

function handleError(error) {
    clearFields()
    checking.value = false
    if (error.response.status === 404) {
        notify('EMAIL_NOT_FOUND', 'danger')
        return
    }
    notify(error.response.status === 401 ? 'WRONG_PASSWORD' : 'SOMETHING_WENT_WRONG', 'danger')
}

function tryToLogIn() {
    checking.value = true
    if (validation.$invalid) {
        notify('EMAIL_PASSWORD_REQUIRED', 'danger')
        validation.$reset()
        checking.value = false
        return undefined
    }

    return authStore
        .logIn({ email: email.value, password: password.value })
        .then(handleSuccess)
        .catch(handleError)
}

usePageHead({ title: 'Log in', description: 'Log in' })
</script>

<template>
    <Layout>
        <BaseContent>
            <BaseSection padded>
                <div class="login-form-container">
                    <h4 class="login-title">Log In</h4>
                    <SocialLogin />
                    <div class="login-line-separator">OR</div>
                    <form
                        class="login-form"
                        @submit.prevent="tryToLogIn"
                    >
                        <div class="login-field-container">
                            <BaseInput
                                v-model="email"
                                :class="['login-field', validation.email.$error && 'login-error']"
                                name="email"
                                autocomplete="current-email"
                                placeholder="email*"
                                @blur="validation.email.$touch()"
                            />
                            <label
                                v-if="validation.email.$error"
                                :class="['login-label', 'login-error']"
                            >This field is required and must be an email</label>
                        </div>
                        <div class="login-field-container">
                            <BaseInput
                                v-model="password"
                                :class="['login-field', validation.password.$error && 'login-error']"
                                name="password"
                                type="password"
                                autocomplete="current-password"
                                placeholder="password*"
                                @blur="validation.password.$touch()"
                            />
                            <label
                                v-if="validation.password.$error"
                                :class="['login-label', 'login-error']"
                            >This field is required</label>
                        </div>
                        <div class="login-button-container">
                            <BaseButton
                                class="login-button"
                                :disabled="checking"
                                button-type="highlight"
                                type="submit"
                                @click="() => {}"
                            >Log In</BaseButton>
                        </div>
                        <div class="login-forgot-password">
                            <div>Forgot your password?</div>
                            <BaseLink
                                class="login-forgot-password-link"
                                :to="{ name: 'forgot-password' }"
                            >Click here to reset your password</BaseLink>
                        </div>
                        <div class="login-agreement">
                            If you log in using Apple, Google, or Facebook and are not already registered,
                            a new account will be created and you will agree to our
                            <a
                                class="login-link"
                                href="https://frisky.fm/about/terms"
                                target="_blank"
                            >Terms and Conditions</a>
                            &
                            <a
                                class="login-link"
                                href="https://frisky.fm/about/privacy-policy"
                                target="_blank"
                            >Privacy Policy</a>.
                        </div>
                        <div class="login-additional-options">
                            <span class="login-additional-text">Don't have an account?</span>
                            <BaseLink :to="{ name: 'signup' }">
                                <BaseButton
                                    button-type="tagBlack"
                                    @click="() => {}"
                                >Sign Up</BaseButton>
                            </BaseLink>
                        </div>
                    </form>
                </div>
            </BaseSection>
        </BaseContent>
    </Layout>
</template>

<style lang="scss" scoped>
@import '@design';

.login-form-container {
    position: relative;
    margin-bottom: $s-h;
    .login-field {
        &.login-error {
            margin-bottom: $s-xl;
            border-color: $c-punch-red;
            border-width: 2px;
        }
    }
}
.login-field-container {
    position: relative;
    width: 100%;
}
.login-form {
    display: flex;
    flex-direction: column;
    align-items: center;
}
.login-label {
    @extend %contentSubtitleM;

    display: block;
    font-weight: 300;
    text-align: left;

    &.login-error {
        position: absolute;
        bottom: $s-xs;
        color: $c-punch-red;
    }
}
.login-title {
    text-align: left;
}
.login-subtitle {
    @extend %contentTinyHeading;

    font-weight: 400;
    text-align: left;
}

.login-line-separator {
    @include lineSeparator(true, transparentize($c-tuna, 0.5));

    @extend %contentSubtitleS;

    margin-bottom: $s-m * 2;
    font-weight: 100;
}

.login-button-container {
    width: 100%;
    text-align: right;
}

.login-button {
    margin-top: 0;
}

.login-agreement {
    @extend %contentSubtitleS;

    margin-top: $s-l;
}

a.login-link {
    color: $c-torch-red;
}

.login-additional {
    &-options {
        margin-top: $s-h;
    }
    &-text {
        @extend %contentSubtitleM;
    }
}

.login-forgot-password {
    @extend %contentSubtitleL;

    color: $c-text-faded;
}
a.login-forgot-password-link {
    font-weight: 600;
    color: $c-highlight;
}

@include desktop {
    .login-additional-text {
        margin-right: $s-xl;
    }
}
</style>
